import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { Save, Upload, FileText, Trash2, Download, Eye } from 'lucide-react';
import { formatName } from '@/lib/utils';
import PhoneField from '@/components/PhoneField';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const MAX_DOC_SIZE = 10 * 1024 * 1024; // 10MB

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const [profile, setProfile] = useState({});
  const [pwForm, setPwForm] = useState({ old_password: '', new_password: '', confirm: '' });
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [viewer, setViewer] = useState(null);
  const [showDelete, setShowDelete] = useState(null);

  useEffect(() => {
    if (user) setProfile({ ...user });
    if (user?.role === 'CLIENT') fetchDocuments();
  }, [user]);

  const fetchDocuments = async () => {
    try {
      const { data } = await api.get('/uploads/medical-documents');
      setDocuments(data);
    } catch {}
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_DOC_SIZE) {
      setError(`File too large (${(file.size/1024/1024).toFixed(1)} MB). Max 10MB per file.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploading(true); setMsg(''); setError(''); setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/uploads/medical-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      setMsg('Document uploaded successfully');
      fetchDocuments();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); setUploadProgress(0); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDeleteDoc = async () => {
    if (!showDelete) return;
    try {
      await api.delete(`/uploads/medical-documents/${showDelete.id}`);
      setShowDelete(null);
      fetchDocuments();
    } catch {}
  };

  const openViewer = async (doc) => {
    try {
      const res = await api.get(`/uploads/medical-documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type || 'application/octet-stream' }));
      setViewer({ url, type: doc.content_type || '', name: doc.original_filename });
    } catch { alert('Could not open document'); }
  };
  const closeViewer = () => { if (viewer?.url) window.URL.revokeObjectURL(viewer.url); setViewer(null); };

  const downloadDoc = async (docId, filename) => {
    try {
      const res = await api.get(`/uploads/medical-documents/${docId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    } catch {}
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setMsg(''); setError('');
    try {
      if (user.role === 'CLIENT') {
        await api.put(`/clients/${user._id}`, {
          phone: profile.phone, address: profile.address, blood_group: profile.blood_group,
          email: profile.email,
          previous_medical_issues: profile.previous_medical_issues,
          menstrual_cycle_info: profile.menstrual_cycle_info,
          pregnancy_history: profile.pregnancy_history,
        });
      }
      setMsg('Profile updated successfully');
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const handleTherapistProfileUpdate = async (e) => {
    e.preventDefault();
    setMsg(''); setError('');
    try {
      await api.put('/therapists/me/profile', {
        phone: profile.phone || '',
        qualification: profile.qualification || '',
        ncahp_id: profile.ncahp_id || '',
        apa_id: profile.apa_id || '',
      });
      if (checkAuth) await checkAuth();
      setMsg('Profile updated successfully');
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMsg(''); setError('');
    if (pwForm.new_password !== pwForm.confirm) { setError('Passwords do not match'); return; }
    try {
      await api.post('/auth/change-password', { old_password: pwForm.old_password, new_password: pwForm.new_password });
      setPwForm({ old_password: '', new_password: '', confirm: '' });
      setMsg('Password changed successfully');
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-page">
      <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Settings</h1>

      {msg && <div className="bg-[#2E8B57]/10 text-[#2E8B57] p-3 rounded-xl text-sm" data-testid="success-msg">{msg}</div>}
      {error && <div className="bg-[#D3544E]/10 text-[#D3544E] p-3 rounded-xl text-sm" data-testid="error-msg">{error}</div>}

      {/* Account Info */}
      <Card className="bg-white border-[#E5F2F1] rounded-2xl">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-[#1A3330] font-['Outfit'] mb-4">Account Information</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div><Label className="text-xs text-[#4A6B68]">Name</Label><p className="text-sm text-[#1A3330]">{formatName(user)}</p></div>
            <div><Label className="text-xs text-[#4A6B68]">Email</Label><p className="text-sm text-[#1A3330]">{user?.email}</p></div>
            <div><Label className="text-xs text-[#4A6B68]">Role</Label><p className="text-sm text-[#1A3330]">{user?.role}</p></div>
            <div><Label className="text-xs text-[#4A6B68]">Phone</Label>
              <PhoneField value={profile.phone || ''} onChange={v => setProfile({ ...profile, phone: v })} testId="settings-phone" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Professional Profile (Therapist / Admin) */}
      {(user?.role === 'THERAPIST' || user?.role === 'ADMIN') && (
        <Card className="bg-white border-[#E5F2F1] rounded-2xl">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-[#1A3330] font-['Outfit'] mb-4">Professional Profile</h3>
            <form onSubmit={handleTherapistProfileUpdate} className="space-y-4">
              <div>
                <Label className="text-sm">Qualification</Label>
                <Input value={profile.qualification || ''} onChange={e => setProfile({ ...profile, qualification: e.target.value })} placeholder="M.Phil Clinical Psych" className="rounded-xl border-[#E5F2F1]" data-testid="settings-qualification" />
              </div>
              <div className="border-t border-[#E5F2F1] pt-4">
                <p className="text-sm font-semibold text-[#1A3330] mb-3">Professional Registrations</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">NCAHP Registration ID</Label>
                    <Input value={profile.ncahp_id || ''} onChange={e => setProfile({ ...profile, ncahp_id: e.target.value })} placeholder="Optional" className="rounded-xl border-[#E5F2F1]" data-testid="settings-ncahp-id" />
                  </div>
                  <div>
                    <Label className="text-sm">APA Membership ID</Label>
                    <Input value={profile.apa_id || ''} onChange={e => setProfile({ ...profile, apa_id: e.target.value })} placeholder="Optional" className="rounded-xl border-[#E5F2F1]" data-testid="settings-apa-id" />
                  </div>
                </div>
              </div>
              <Button type="submit" className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-therapist-profile-btn"><Save className="w-4 h-4 mr-2" />Save Changes</Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Medical Info (Client only) */}
      {user?.role === 'CLIENT' && (
        <Card className="bg-white border-[#E5F2F1] rounded-2xl">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-[#1A3330] font-['Outfit'] mb-4">Medical Information</h3>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-sm">Address</Label>
                  <Textarea value={profile.address || ''} onChange={e => setProfile({...profile, address: e.target.value})} className="rounded-xl border-[#E5F2F1]" rows={2} data-testid="settings-address" />
                </div>
                <div><Label className="text-sm">Blood Group</Label>
                  <Select value={profile.blood_group || ''} onValueChange={v => setProfile({...profile, blood_group: v})}>
                    <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="settings-blood-group"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-sm">Previous Medical Issues</Label>
                <Textarea value={profile.previous_medical_issues || ''} onChange={e => setProfile({...profile, previous_medical_issues: e.target.value})} className="rounded-xl border-[#E5F2F1]" rows={2} data-testid="settings-medical-issues" />
              </div>
              <div><Label className="text-sm">Menstrual Cycle Info (Optional)</Label>
                <Textarea value={profile.menstrual_cycle_info || ''} onChange={e => setProfile({...profile, menstrual_cycle_info: e.target.value})} className="rounded-xl border-[#E5F2F1]" rows={2} />
              </div>
              <div><Label className="text-sm">Pregnancy History (Optional)</Label>
                <Textarea value={profile.pregnancy_history || ''} onChange={e => setProfile({...profile, pregnancy_history: e.target.value})} className="rounded-xl border-[#E5F2F1]" rows={2} />
              </div>
              <Button type="submit" className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-profile-btn"><Save className="w-4 h-4 mr-2" />Save Changes</Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Medical Documents (Client only) */}
      {user?.role === 'CLIENT' && (
        <Card className="bg-white border-[#E5F2F1] rounded-2xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#1A3330] font-['Outfit']">Medical Documents <span className="text-xs font-normal text-[#4A6B68]">({documents.length} files)</span></h3>
              <div>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="doc-file-input" />
                <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl text-sm" data-testid="upload-doc-btn">
                  <Upload className="w-4 h-4 mr-2" />{uploading ? `Uploading ${uploadProgress}%...` : 'Upload Document'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-[#4A6B68] mb-3">Upload medical documents (PDF, JPEG, PNG). Max 10MB per file. Visible to your assigned therapist.</p>
            {uploading && (
              <div className="mb-3 bg-[#E5F2F1] rounded-full h-2 overflow-hidden" data-testid="upload-progress">
                <div className="bg-[#0D8B7D] h-2 transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
            )}
            <div className="space-y-2">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-[#F4FAF9] rounded-xl" data-testid={`settings-doc-row-${doc.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-[#0D8B7D] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-[#1A3330] truncate">{doc.original_filename}</p>
                      <p className="text-[10px] text-[#4A6B68]">{(doc.size / 1024).toFixed(1)} KB · {new Date(doc.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => openViewer(doc)} className="text-[#0D8B7D] h-7" data-testid={`view-doc-${doc.id}`}><Eye className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => downloadDoc(doc.id, doc.original_filename)} className="text-[#0D8B7D] h-7" data-testid={`download-doc-${doc.id}`}><Download className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowDelete(doc)} className="text-[#D3544E] h-7" data-testid={`delete-doc-${doc.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
              {documents.length === 0 && <p className="text-sm text-[#4A6B68] text-center py-3">No documents uploaded yet.</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document viewer modal */}
      <Dialog open={!!viewer} onOpenChange={(o) => { if (!o) closeViewer(); }}>
        <DialogContent className="max-w-4xl bg-white rounded-2xl p-0 overflow-hidden" data-testid="settings-doc-viewer">
          <DialogHeader className="px-6 py-4 border-b border-[#E5F2F1]">
            <DialogTitle className="font-['Outfit'] text-[#1A3330] truncate">{viewer?.name}</DialogTitle>
          </DialogHeader>
          {viewer && viewer.type.includes('pdf') && <iframe src={viewer.url} title="PDF" className="w-full h-[75vh]" />}
          {viewer && viewer.type.startsWith('image/') && (
            <div className="overflow-auto max-h-[75vh] flex items-center justify-center bg-[#F4FAF9]">
              <img src={viewer.url} alt={viewer.name} className="max-w-full transition-transform hover:scale-150 cursor-zoom-in" />
            </div>
          )}
          {viewer && !viewer.type.includes('pdf') && !viewer.type.startsWith('image/') && (
            <div className="p-6 text-center text-[#4A6B68]">Cannot preview this file type.</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm modal */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent className="max-w-sm bg-white rounded-2xl" data-testid="delete-doc-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#D3544E]">Delete Document</DialogTitle></DialogHeader>
          {showDelete && (
            <div className="space-y-3">
              <p className="text-sm text-[#4A6B68]">Are you sure you want to delete this document?</p>
              <div className="bg-[#D3544E]/5 p-3 rounded-xl">
                <p className="text-sm font-medium text-[#1A3330]">{showDelete.original_filename}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleDeleteDoc} className="flex-1 bg-[#D3544E] hover:bg-[#C04440] text-white rounded-xl" data-testid="confirm-delete-doc">Confirm Delete</Button>
                <Button variant="outline" onClick={() => setShowDelete(null)} className="rounded-xl border-[#E5F2F1]">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Password */}
      <Card className="bg-white border-[#E5F2F1] rounded-2xl">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-[#1A3330] font-['Outfit'] mb-4">Change Password</h3>
          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-sm">
            <div><Label className="text-sm">Current Password</Label><Input type="password" value={pwForm.old_password} onChange={e => setPwForm({...pwForm, old_password: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="current-password" /></div>
            <div><Label className="text-sm">New Password</Label><Input type="password" value={pwForm.new_password} onChange={e => setPwForm({...pwForm, new_password: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="new-password" /></div>
            <div><Label className="text-sm">Confirm New Password</Label><Input type="password" value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="confirm-password" /></div>
            <Button type="submit" className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="change-password-btn">Change Password</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
